import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface SpiritLampProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  flameOn?: boolean;
  /** 0..1 multiplier for flame size. Default 1.0. */
  intensity?: number;
}

/**
 * Spirit lamp — a small reservoir of methylated spirit with a fabric
 * wick poking through the top, used to heat a horizontal glass tube
 * from below in the CuO+H₂ reduction experiment. The flame is a soft
 * yellow-orange (cooler than a Bunsen burner) and gently flickers.
 */
export function SpiritLamp({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  flameOn = false,
  intensity = 1.0,
}: SpiritLampProps) {
  const flameInnerRef = useRef<THREE.Mesh>(null);
  const flameOuterRef = useRef<THREE.Mesh>(null);
  const lightRef = useRef<THREE.PointLight>(null);

  useFrame((s) => {
    if (!flameOn) return;
    const t = s.clock.elapsedTime * 8;
    const flicker = Math.sin(t) * 0.06 + Math.sin(t * 2.3) * 0.04;
    if (flameInnerRef.current) {
      const sy = intensity * (1 + flicker * 0.4);
      const sx = 0.6 + intensity * 0.4 + flicker * 0.1;
      flameInnerRef.current.scale.set(sx, sy, sx);
    }
    if (flameOuterRef.current) {
      const sy = intensity * (1.1 + flicker * 0.5);
      const sx = 0.7 + intensity * 0.45 + flicker * 0.12;
      flameOuterRef.current.scale.set(sx, sy, sx);
    }
    if (lightRef.current) {
      lightRef.current.intensity = (0.6 + flicker * 0.25) * intensity;
    }
  });

  const bodyRadius = 0.045;
  const bodyHeight = 0.07;
  const neckRadius = 0.012;
  const neckHeight = 0.015;

  return (
    <group position={position} rotation={rotation} scale={scale}>
      {/* Glass reservoir body — pale blue tint */}
      <mesh position={[0, bodyHeight / 2, 0]} castShadow receiveShadow>
        <cylinderGeometry args={[bodyRadius * 0.85, bodyRadius, bodyHeight, 28]} />
        <meshPhysicalMaterial
          color="#c9d8e6"
          transparent
          opacity={0.55}
          transmission={0.7}
          roughness={0.15}
          ior={1.45}
          thickness={0.01}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Methylated spirit liquid inside */}
      <mesh position={[0, bodyHeight * 0.35, 0]}>
        <cylinderGeometry args={[bodyRadius * 0.78, bodyRadius * 0.85, bodyHeight * 0.55, 24]} />
        <meshPhysicalMaterial
          color="#9bb6cd"
          transparent
          opacity={0.75}
          transmission={0.4}
          roughness={0.05}
          ior={1.38}
        />
      </mesh>
      {/* Brass collar at the top */}
      <mesh position={[0, bodyHeight + neckHeight / 2, 0]} castShadow>
        <cylinderGeometry args={[neckRadius * 1.6, neckRadius * 1.6, neckHeight, 18]} />
        <meshStandardMaterial color="#c89c46" metalness={0.8} roughness={0.3} />
      </mesh>
      {/* Wick — small fabric stub */}
      <mesh position={[0, bodyHeight + neckHeight + 0.008, 0]}>
        <cylinderGeometry args={[0.005, 0.006, 0.016, 12]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.95} />
      </mesh>

      {/* Flame */}
      {flameOn && (
        <group position={[0, bodyHeight + neckHeight + 0.025, 0]}>
          {/* Inner bright core */}
          <mesh ref={flameInnerRef} position={[0, 0.02, 0]}>
            <coneGeometry args={[0.006, 0.05, 12]} />
            <meshBasicMaterial
              color="#fff3c4"
              transparent
              opacity={0.85}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          {/* Outer warm envelope */}
          <mesh ref={flameOuterRef} position={[0, 0.035, 0]}>
            <coneGeometry args={[0.012, 0.08, 16]} />
            <meshBasicMaterial
              color="#ffaa44"
              transparent
              opacity={0.55}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          {/* Glow */}
          <mesh position={[0, 0.03, 0]}>
            <sphereGeometry args={[0.04, 12, 12]} />
            <meshBasicMaterial
              color="#ff8833"
              transparent
              opacity={0.18}
              blending={THREE.AdditiveBlending}
              depthWrite={false}
            />
          </mesh>
          <pointLight ref={lightRef} color="#ffcc88" distance={0.6} decay={2} intensity={0} />
        </group>
      )}
    </group>
  );
}

export default SpiritLamp;
